import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Menu, MenuItem, Stack, styled, type SxProps, type Theme } from "@mui/material";
import { useRef, useState, type ReactNode } from "react";
import { Chip } from "~/common/mui/Chip/Chip";

export interface SelectChipOption {
  label: ReactNode;
  selectedLabel?: ReactNode;
  value: string;
  disabled?: boolean;
}

export interface SelectChipProps {
  className?: string;
  value: string;
  values: SelectChipOption[];
  onChange: (event: React.MouseEvent<HTMLLIElement>, value: string) => void;
  disabled?: boolean;
  showSelectedIcon?: boolean;
  filterLabel?: ReactNode;
  clearLabel?: string;
  onClear?: () => void;
  endAdornment?: ReactNode;
  sx?: SxProps<Theme>;
}

const getDisplayLabel = (
  filterLabel: ReactNode | undefined,
  selectedOption: SelectChipOption | undefined,
  value: string,
) => {
  if (filterLabel === undefined) {
    return selectedOption?.label || value;
  }
  if (!selectedOption) {
    return filterLabel;
  }
  return (
    <>
      {filterLabel} = {selectedOption.selectedLabel ?? selectedOption.label}
    </>
  );
};

const SelectChipLabel = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(0.5),
  minWidth: 0,
}));

const SelectChipLabelText = styled("span")({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const EndAdornment = styled("span")({
  display: "inline-flex",
  flexShrink: 0,
  pointerEvents: "auto",
});

export const SelectChip = ({
  className,
  value,
  values,
  onChange,
  showSelectedIcon = false,
  disabled = false,
  filterLabel,
  clearLabel,
  onClear,
  endAdornment,
  sx,
}: SelectChipProps) => {
  const [open, setOpen] = useState(false);
  const chipRef = useRef<HTMLDivElement | null>(null);

  // Find the current selected option
  const selectedOption = values.find((option) => option.value === value);
  const filterPresentation = filterLabel !== undefined;
  const displayLabel = getDisplayLabel(filterLabel, selectedOption, value);
  const chipLabel = endAdornment ? (
    <SelectChipLabel>
      <SelectChipLabelText>{displayLabel}</SelectChipLabelText>
      <EndAdornment onMouseDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
        {endAdornment}
      </EndAdornment>
    </SelectChipLabel>
  ) : (
    displayLabel
  );

  const handleItemClick = (event: React.MouseEvent<HTMLLIElement>, selectedValue: string) => {
    onChange(event, selectedValue);
    setOpen(false);
  };

  const toggleMenu = () => {
    if (!disabled) {
      setOpen((prev) => !prev);
    }
  };

  return (
    <>
      <Chip
        className={className}
        variant={filterPresentation ? "filter" : "assist"}
        selected={filterPresentation && Boolean(selectedOption)}
        disabled={disabled}
        label={chipLabel}
        icon={filterPresentation && !selectedOption ? <AddIcon aria-hidden /> : undefined}
        deleteIcon={
          filterPresentation && selectedOption ? (
            <CloseIcon aria-label={clearLabel} />
          ) : !filterPresentation ? (
            <ArrowDropDownIcon sx={{ transform: open ? "rotate(180deg)" : "" }} />
          ) : undefined
        }
        onDelete={filterPresentation ? (selectedOption && onClear ? onClear : undefined) : toggleMenu}
        onClick={toggleMenu}
        ref={chipRef}
        sx={
          filterPresentation
            ? [
                (theme) => ({
                  height: theme.spacing(4),
                  "& .MuiChip-icon, & .MuiChip-deleteIcon": {
                    width: theme.spacing(2.25),
                    height: theme.spacing(2.25),
                  },
                  "& .MuiChip-deleteIcon": {
                    order: -1,
                    marginLeft: theme.spacing(1),
                    marginRight: theme.spacing(-0.5),
                  },
                }),
                ...(Array.isArray(sx) ? sx : [sx]),
              ]
            : sx
        }
      />
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={chipRef.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {values.map(({ label, value: optionValue, disabled: optionDisabled }) => {
          const isSelected = optionValue === value;
          return (
            <MenuItem
              key={optionValue}
              onClick={(event) => handleItemClick(event, optionValue)}
              selected={isSelected}
              disabled={optionDisabled}
            >
              <Stack direction={"row"} gap={1.5} justifyContent={"space-between"}>
                {label}
                {showSelectedIcon && isSelected && <CheckIcon fontSize="medium" color="onSecondaryContainer" />}
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
